import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import {
  ClipboardCheck,
  ScanEye,
  User,
  ChevronRight,
  ClipboardList,
  MapPin,
} from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import {
  asList as asListShared,
  buildChartIndex as buildChartIndexShared,
  hasOralExam as hasOralExamShared,
  getDoctorImage,
} from '../../lib/chart-helpers.js'
import { usePatientLive } from '../../lib/patientLive.js'
import { fetchAllTreatments } from '../../lib/billing.js'
import {
  Loading, EmptyState, ErrorState, useBranches, branchName, branchNameFromText,
  dateParts, fmtMonth, TH_DAYS_SHORT,
} from '../../kit'

export default function PatientChartHistory() {
  const { id } = useParams()
  const { live } = useOutletContext() || {}
  // Bumped by a realtime event → refetches in place.
  const [tick, setTick] = useState(0)
  const [treatments, setTreatments] = useState(null)
  const [charts, setCharts]         = useState(null)
  const [error, setError]           = useState(null)

  // Live: the doctor writing Hx in the doctor portal, or a visit created at the
  // counter, both land here. No clearing on refetch — the list stays on screen.
  usePatientLive(live, ['chart', 'treatment'], () => setTick((t) => t + 1))

  useEffect(() => {
    setTreatments(null)
    setCharts(null)
    setError(null)
  }, [id])

  useEffect(() => {
    let alive = true
    Promise.allSettled([
      fetchAllTreatments(id),
      apiGet('/api/charts', { patient_id: id }),
    ]).then(([tRes, cRes]) => {
      if (!alive) return
      if (tRes.status === 'fulfilled') setTreatments(tRes.value)
      else { setError(tRes.reason?.message || 'โหลด treatment ไม่สำเร็จ'); setTreatments([]) }
      if (cRes.status === 'fulfilled') setCharts(asList(cRes.value))
      else setCharts([])
    })
    return () => { alive = false }
  }, [id, tick])

  // Branch of the visit. The treatment row may carry a joined `branch` object, a
  // `branch_id`, or nothing — so we keep an id→name map as the fallback. Cached, so
  // this costs one request per session no matter how many patients are opened.
  const branches = useBranches()
  const branchNameOf = useMemo(() => (t) => {
    const direct = (typeof t?.branch === 'object' ? t?.branch?.name : null) || t?.branch_name
    if (direct) return branchNameFromText(direct)  // joined NAME — สนามเป้า → อารีย์, UI only
    const bid = t?.branch_id ?? (typeof t?.branch === 'object' ? t?.branch?.id : t?.branch)
    if (bid == null || bid === '') return ''
    return branchName(bid, branches)
  }, [branches])

  const chartIndex = useMemo(() => buildChartIndex(charts || []), [charts])
  const groups     = useMemo(() => groupByMonth(treatments || []), [treatments])
  const loading    = treatments === null

  const stats = useMemo(() => {
    if (!treatments) return null
    const total       = treatments.length
    const withChart   = treatments.filter((t) => !!chartIndex.findFor(t)).length
    const withOral    = treatments.filter((t) => hasOralExam(chartIndex.findFor(t))).length
    return { total, withChart, withOral }
  }, [treatments, chartIndex])

  // Exclusive states: first load · error · empty · list. A live refetch keeps
  // the list on screen (treatments is never reset to null by it).
  return (
    <div>
      {/* Counts strip — the title and tabs come from ChartLayout's PageHeader. */}
      {!loading && !error && stats && stats.total > 0 && (
        <div className="flex items-center gap-4 flex-wrap text-[13.5px] text-slate-500 mb-5">
          <span><span className="font-semibold text-slate-700 tabular-nums">{stats.total}</span> visits</span>
          <Stat dotClass="bg-emerald-500" label="มี chart" value={stats.withChart} />
          <Stat dotClass="bg-sky-500"     label="มี oral exam" value={stats.withOral} />
        </div>
      )}

      {loading && <Loading />}
      {!loading && error && <ErrorState message={error} onRetry={() => { setError(null); setTreatments(null); setTick((t) => t + 1) }} />}
      {!loading && !error && treatments?.length === 0 && (
        <EmptyState icon={ClipboardList} title="ยังไม่มี treatment" hint="เมื่อมีการบันทึกการรักษา จะแสดงในรายการนี้" />
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.key}>
              <div className="flex items-center gap-3 mb-2.5 px-1">
                <h3 className="text-[14px] font-semibold text-slate-700 whitespace-nowrap">
                  {fmtMonth(g.iso, { long: true })}
                </h3>
                <span className="text-[13px] text-slate-400 font-medium whitespace-nowrap">{g.items.length} visits</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                {g.items.map((t, i) => (
                  <TreatmentRow
                    key={t.id ?? t.treatment_id}
                    patientId={id}
                    t={t}
                    chart={chartIndex.findFor(t)}
                    branch={branchNameOf(t)}
                    isLast={i === g.items.length - 1}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ dotClass, label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span>{label}</span>
      <span className="font-semibold text-slate-700 tabular-nums">{value}</span>
    </span>
  )
}


/* -------------------------------------------------------------------------- */
/* Row                                                                        */
/* -------------------------------------------------------------------------- */

function TreatmentRow({ patientId, t, chart, branch, isLast }) {
  const tid       = t.id ?? t.treatment_id
  const hasChart  = !!chart
  const hasOral   = hasOralExam(chart)

  const dp        = dateParts(t.date)
  const day       = dp ? String(dp.d).padStart(2, '0') : '--'
  const weekday   = dp ? TH_DAYS_SHORT[dp.dow] : ''

  const al        = t.appointmentlist || t.appointmentList || {}
  const alType    = al.type || t.type || '—'
  const alDetail  = al.detail || ''
  const alColor   = al.color
  const accent    = accentColor(alColor)

  const detail    = t.detail || t.details || ''
  const doctor    = t.doctor || {}
  // Xano's `doctor.code` is the display name (e.g. "เอก ธนิตย์"), not a short code.
  const docName   = (doctor.code || doctor.name || doctor.full_name || '').trim()
  const docImg    = getDoctorImage(t)

  return (
    <Link
      to={`/patient/${patientId}/chart/history/${tid}`}
      state={{ treatment: t, chart }}
      className={`group relative flex items-center gap-4 pl-5 pr-3.5 py-3.5 hover:bg-slate-50/70 transition-colors ${
        isLast ? '' : 'border-b border-slate-100'
      }`}
    >
      {/* Color accent strip (appointment type) */}
      <span
        aria-hidden
        className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-opacity group-hover:opacity-100 opacity-80"
        style={{ backgroundColor: accent }}
      />

      {/* Date */}
      <div className="flex-none w-10 text-center">
        <div className="text-[19px] font-semibold text-slate-900 leading-none tabular-nums">{day}</div>
        <div className="text-[12px] text-slate-400 font-medium mt-1">{weekday}</div>
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <AppointmentChip type={alType} detail={alDetail} color={alColor} />
          <StatusDots hasChart={hasChart} hasOral={hasOral} />
        </div>
        {detail && (
          <div className="text-[14px] text-slate-500 mt-1 line-clamp-1">{detail}</div>
        )}
      </div>

      {/* Branch of this visit — fixed width, same alignment contract as the doctor
          column. Hidden below md so the row doesn't crush on narrow screens; the
          full name rides in the title for truncated labels. */}
      <div className="flex-none hidden md:flex items-center gap-1.5 w-[132px] min-w-0" title={branch || undefined}>
        {branch
          ? <>
              <MapPin size={13} className="flex-none text-slate-300" strokeWidth={2} />
              <span className="text-[13.5px] text-slate-500 truncate leading-tight">{branch}</span>
            </>
          : <span className="text-[13.5px] text-slate-300">—</span>}
      </div>

      {/* Doctor — fixed width so all rows align */}
      <div className="flex-none hidden sm:flex items-center gap-2 w-[140px]">
        <DoctorAvatar src={docImg} name={docName} />
        <span className="text-[14px] font-medium text-slate-600 truncate leading-tight">{docName || '—'}</span>
      </div>

      <ChevronRight
        size={14}
        className="flex-none text-slate-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all"
      />
    </Link>
  )
}

function StatusDots({ hasChart, hasOral }) {
  if (!hasChart && !hasOral) return null
  return (
    <span className="inline-flex items-center gap-1 ml-0.5">
      {hasChart && (
        <span title="มี chart" className="inline-flex items-center gap-0.5 text-emerald-600">
          <ClipboardCheck size={11.5} strokeWidth={2.25} />
        </span>
      )}
      {hasOral && (
        <span title="มี oral examination" className="inline-flex items-center gap-0.5 text-sky-600">
          <ScanEye size={11.5} strokeWidth={2.25} />
        </span>
      )}
    </span>
  )
}

// Pull a usable hex for the accent strip from the appointmentlist color,
// fallback to a soft slate when none is set.
function accentColor(hex) {
  if (!hex || typeof hex !== 'string') return '#cbd5e1'
  const h = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#cbd5e1'
  return `#${h}`
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

function DoctorAvatar({ src, name }) {
  const initial = doctorInitial(name)
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-none ring-1 ring-slate-200 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50">
      {src
        ? <img src={src} alt={name || ''} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        : (initial
            ? <span className="text-[13.5px] font-semibold text-slate-500">{initial}</span>
            : <User size={13} className="text-slate-400" strokeWidth={1.75} />)}
    </div>
  )
}

// Avatar initial — shared rule (skips Thai pre-posed vowels/tone marks).
const doctorInitial = nameInitial

function AppointmentChip({ type, detail, color }) {
  const styles = chipStyles(color)
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[13.5px] max-w-full align-middle"
      style={styles}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ backgroundColor: styles.color }} />
      <span className="font-semibold whitespace-nowrap">{type}</span>
      {detail && <span className="opacity-80 truncate">· {detail}</span>}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Re-export shared helpers locally so we don't have to touch every call site.
const asList = asListShared

// Sortable number from the visit date, read as Bangkok wall-clock (kit dateParts).
function dateSortKey(v) {
  const p = dateParts(v)
  return p ? p.y * 1e8 + p.m * 1e6 + p.d * 1e4 + (p.hh ?? 0) * 100 + (p.mi ?? 0) : 0
}

function groupByMonth(treatments) {
  const map = new Map()
  const out = []
  for (const t of treatments) {
    const p = dateParts(t.date)
    if (!p) continue
    const key = `${p.y}-${String(p.m).padStart(2, '0')}`
    if (!map.has(key)) {
      const g = { key, iso: `${key}-01`, year: p.y, month: p.m, items: [] }
      map.set(key, g)
      out.push(g)
    }
    map.get(key).items.push(t)
  }
  // Within each month: newest day first
  for (const g of out) g.items.sort((a, b) => dateSortKey(b.date) - dateSortKey(a.date))
  // Months newest first
  out.sort((a, b) => (b.year - a.year) || (b.month - a.month))
  return out
}

const buildChartIndex = buildChartIndexShared
const hasOralExam     = hasOralExamShared


/** Build inline styles for the appointmentlist chip from its hex color. */
function chipStyles(hex) {
  if (!hex || typeof hex !== 'string' || !/^#?[0-9a-fA-F]{6}$/.test(hex.replace('#', ''))) {
    return { backgroundColor: '#f1f5f9', color: '#475569', boxShadow: 'inset 0 0 0 1px rgba(148,163,184,0.25)' }
  }
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const bg   = `rgba(${r}, ${g}, ${b}, 0.10)`
  const ring = `rgba(${r}, ${g}, ${b}, 0.28)`
  const text = `rgb(${Math.round(r * 0.55)}, ${Math.round(g * 0.55)}, ${Math.round(b * 0.55)})`
  return { backgroundColor: bg, color: text, boxShadow: `inset 0 0 0 1px ${ring}` }
}
